import { FilterDropdown } from "@refinedev/antd";
import { Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { VesselConsumption } from "../lib/types";
import { ConsumptionPctCell } from "./ConsumptionPctCell";

const money = (v: number) =>
  v.toLocaleString("en-US", { maximumFractionDigits: 2 });

// This table is client-side (plain array prop, no useTable), so the
// project-standard FilterDropdown multi-selects (see vessel/list.tsx) pair
// with column onFilter and draw their options from the rows themselves.
const toFilterOptions = (values: Array<string | null>) =>
  Array.from(new Set(values.filter((v): v is string => v !== null)))
    .sort()
    .map((v) => ({ label: v, value: v }));

interface VesselConsumptionTableProps {
  vessels: VesselConsumption[];
  loading: boolean;
  onVesselClick: (v: VesselConsumption) => void;
}

export const VesselConsumptionTable: React.FC<VesselConsumptionTableProps> = ({
  vessels,
  loading,
  onVesselClick,
}) => {
  const columns: ColumnsType<VesselConsumption> = [
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            options={toFilterOptions(vessels.map((v) => v.vesselName))}
          />
        </FilterDropdown>
      ),
      onFilter: (value, record) => record.vesselName === value,
    },
    {
      title: "IMO",
      dataIndex: "imoNumber",
      key: "imoNumber",
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO"
            options={toFilterOptions(vessels.map((v) => v.imoNumber))}
          />
        </FilterDropdown>
      ),
      onFilter: (value, record) => record.imoNumber === value,
    },
    {
      title: "Shipco",
      key: "shipcoName",
      render: (_, record) => record.shipcoName ?? "-",
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            options={toFilterOptions(vessels.map((v) => v.shipcoName))}
          />
        </FilterDropdown>
      ),
      onFilter: (value, record) => record.shipcoName === value,
    },
    {
      title: "Budget",
      key: "budget",
      render: (_, record) => `${money(record.budget)} ${record.budgetCurrency}`,
    },
    {
      title: "Spend",
      dataIndex: "spend",
      key: "spend",
      render: (value) => money(value),
    },
    {
      title: "Consumption %",
      key: "consumptionPct",
      render: (_, record) => (
        <ConsumptionPctCell
          pct={record.consumptionPct}
          budgetPending={record.budgetPending}
        />
      ),
    },
    {
      title: "",
      key: "tags",
      render: (_, record) =>
        record.currencyMismatch ? (
          <Tag color="warning">currency mismatch</Tag>
        ) : null,
    },
  ];

  return (
    <Table<VesselConsumption>
      rowKey="vesselId"
      loading={loading}
      dataSource={vessels}
      columns={columns}
      pagination={{ pageSize: 25 }}
      onRow={(record) => ({
        onClick: () => onVesselClick(record),
        style: { cursor: "pointer" },
      })}
    />
  );
};
